import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PiggyBankDisplay } from '../components/shared/PiggyBankDisplay';
import { TransactionList } from '../components/shared/TransactionList';
import { SavingsGoals } from '../components/shared/SavingsGoals';
import { AchievementBadges } from '../components/shared/AchievementBadges';
import { SpendingCategories, SPENDING_CATEGORIES } from '../components/shared/SpendingCategories';
import { InterestCalculator } from '../components/shared/InterestCalculator';
import { MyChores } from '../components/shared/ChoreRewards';
import SavingsPots from '../components/shared/SavingsPots';
import { AllocationSlider, AllocationPreview } from '../components/shared/AllocationSlider';
import { 
  Wallet, LogOut, Plus, ArrowUpCircle, ArrowDownCircle,
  Sparkles, PiggyBank, Loader2, Heart, ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { user, logout, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    amount: '',
    type: 'deposit',
    description: '',
    category: '',
    donation_recipient: ''
  });

  // Balance distribution state
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [distributeAllocation, setDistributeAllocation] = useState({ spend: 70, save: 20, give: 10 });
  const [distributing, setDistributing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setGoalsLoading(true);
      const [txRes, goalsRes] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/goals`)
      ]);
      setTransactions(txRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setGoalsLoading(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data);
    } catch (error) {
      toast.error('Failed to fetch goals');
    }
  };

  const handleCreateGoal = async (goalData) => {
    try {
      await axios.post(`${API}/goals`, goalData);
      toast.success('Goal created! 🎯');
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create goal');
      throw error;
    }
  };

  const handleUpdateGoal = async (goalId, goalData) => {
    try {
      await axios.patch(`${API}/goals/${goalId}`, goalData);
      toast.success('Goal updated!');
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update goal');
      throw error;
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`);
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete goal');
    }
  };

  const handleSubmitTransaction = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(newTransaction.amount),
        type: newTransaction.type,
        description: newTransaction.description || null,
        category: (newTransaction.type === 'withdrawal' || newTransaction.type === 'donation') 
          ? (newTransaction.category || null) 
          : null,
        donation_recipient: newTransaction.type === 'donation' 
          ? (newTransaction.donation_recipient || null) 
          : null
      };
      
      await axios.post(`${API}/transactions`, payload);
      
      const successMsg = newTransaction.type === 'donation' 
        ? 'Donation request sent! What a kind thing to do! 💝'
        : 'Request sent to your parent for approval!';
      toast.success(successMsg);
      
      setDialogOpen(false);
      setNewTransaction({ amount: '', type: 'deposit', description: '', category: '', donation_recipient: '' });
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle balance distribution to pots
  const handleDistributeBalance = async () => {
    setDistributing(true);
    try {
      const res = await axios.post(`${API}/pots/distribute-balance`, {
        spend_percent: distributeAllocation.spend,
        save_percent: distributeAllocation.save,
        give_percent: distributeAllocation.give
      });
      toast.success(res.data.message);
      setDistributeOpen(false);
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute balance');
    } finally {
      setDistributing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-purple-50 to-pink-100">
      {/* Fun Header */}
      <header className="bg-gradient-to-r from-sky-400 to-purple-500 text-white sticky top-0 z-10 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur rounded-xl sm:rounded-2xl flex items-center justify-center animate-bounce shrink-0" style={{ animationDuration: '2s' }}>
              <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2 truncate">
                My Wallet <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300 shrink-0" />
              </h1>
              <p className="text-sky-100 text-xs sm:text-sm truncate">{user?.username}</p>
            </div>
          </div>
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleLogout} 
            className="bg-white/20 hover:bg-white/30 text-white border-0 px-2 sm:px-3 shrink-0"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Piggy Bank Balance Display */}
        <PiggyBankDisplay 
          balance={user?.balance || 0} 
          className="mb-8"
        />

        {/* Balance to Pots Distribution Prompt */}
        {user?.balance > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="distribute-prompt">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-amber-500 shrink-0" />
                  <div>
                    <p className="text-amber-800 text-sm font-medium">
                      You have ${user.balance.toFixed(2)} waiting to be organized!
                    </p>
                    <p className="text-amber-600 text-xs">
                      Move it to your Spend, Save, and Give pots
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm"
                  onClick={() => setDistributeOpen(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                  data-testid="distribute-balance-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-1" />
                  Distribute Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Not Linked Warning */}
        {!user?.linked_banker_id && (
          <Card className="mb-6 bg-amber-50 border-amber-200" data-testid="link-warning">
            <CardContent className="pt-6">
              <p className="text-amber-800 text-sm flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <span>
                  <strong>Tip:</strong> Ask your parent for their Banker ID to link your accounts! 
                  Then they can approve your money requests.
                </span>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Request Money Button */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="w-full mb-8 py-6 text-lg bg-gradient-to-r from-sky-500 to-purple-500 hover:from-sky-600 hover:to-purple-600 shadow-lg rounded-2xl transform hover:scale-[1.02] transition-all"
              data-testid="new-transaction-btn"
            >
              <Plus className="w-6 h-6 mr-2" />
              Request Money
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="transaction-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Money Request
              </DialogTitle>
              <DialogDescription>
                {user?.linked_banker_id 
                  ? "Your request will be sent to your parent for approval"
                  : "Link your account to a parent to get approvals"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitTransaction} className="space-y-4 mt-4">
              {/* Transaction Type Selection */}
              <div className="space-y-2">
                <Label>What would you like to do?</Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    data-testid="type-deposit"
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'deposit', category: '', donation_recipient: '' })}
                    className={`p-3 rounded-xl border-2 transition-all transform hover:scale-105 ${
                      newTransaction.type === 'deposit'
                        ? 'border-green-500 bg-green-50 shadow-md'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <ArrowDownCircle className={`w-6 h-6 mx-auto mb-1 ${newTransaction.type === 'deposit' ? 'text-green-600' : 'text-slate-400'}`} />
                    <p className={`text-xs font-medium ${newTransaction.type === 'deposit' ? 'text-green-700' : 'text-slate-600'}`}>
                      Deposit
                    </p>
                  </button>
                  <button
                    type="button"
                    data-testid="type-withdrawal"
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'withdrawal', donation_recipient: '' })}
                    className={`p-3 rounded-xl border-2 transition-all transform hover:scale-105 ${
                      newTransaction.type === 'withdrawal'
                        ? 'border-red-500 bg-red-50 shadow-md'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <ArrowUpCircle className={`w-6 h-6 mx-auto mb-1 ${newTransaction.type === 'withdrawal' ? 'text-red-600' : 'text-slate-400'}`} />
                    <p className={`text-xs font-medium ${newTransaction.type === 'withdrawal' ? 'text-red-700' : 'text-slate-600'}`}>
                      Spend
                    </p>
                  </button>
                  <button
                    type="button"
                    data-testid="type-donation"
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'donation', category: 'giving' })}
                    className={`p-3 rounded-xl border-2 transition-all transform hover:scale-105 ${
                      newTransaction.type === 'donation'
                        ? 'border-pink-500 bg-pink-50 shadow-md'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Heart className={`w-6 h-6 mx-auto mb-1 ${newTransaction.type === 'donation' ? 'text-pink-600' : 'text-slate-400'}`} />
                    <p className={`text-xs font-medium ${newTransaction.type === 'donation' ? 'text-pink-700' : 'text-slate-600'}`}>
                      Give
                    </p>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">How much? ($)</Label>
                <Input
                  id="amount"
                  data-testid="transaction-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={newTransaction.amount}
                  onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  className="text-xl text-center py-6"
                />
              </div>

              {/* Category Selection for Withdrawals */}
              {newTransaction.type === 'withdrawal' && (
                <div className="space-y-2">
                  <Label>What's it for? (category)</Label>
                  <Select 
                    value={newTransaction.category}
                    onValueChange={(v) => setNewTransaction({ ...newTransaction, category: v })}
                  >
                    <SelectTrigger data-testid="select-category">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPENDING_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <span className="flex items-center gap-2">
                            <span>{cat.icon}</span>
                            <span>{cat.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Donation Recipient Field */}
              {newTransaction.type === 'donation' && (
                <div className="space-y-2">
                  <Label>Who are you giving to?</Label>
                  <Input
                    data-testid="donation-recipient"
                    value={newTransaction.donation_recipient}
                    onChange={(e) => setNewTransaction({ ...newTransaction, donation_recipient: e.target.value })}
                    placeholder="e.g., Animal Shelter, School Fundraiser..."
                  />
                  <p className="text-xs text-slate-500">
                    💝 Great job wanting to help others!
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Notes (optional)</Label>
                <Input
                  id="description"
                  data-testid="transaction-description"
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                  placeholder={newTransaction.type === 'deposit' ? "Birthday money, Allowance..." : "New toy, Snacks..."}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full py-6 bg-gradient-to-r from-sky-500 to-purple-500 hover:from-sky-600 hover:to-purple-600 text-lg"
                disabled={submitting}
                data-testid="submit-transaction"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Savings Pots */}
        <div className="mb-8">
          <SavingsPots 
            token={token} 
            userId={user?.id}
            onBalanceUpdate={refreshUser}
          />
        </div>

        {/* Two Column Layout for Goals and Badges */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Savings Goals Section */}
          <SavingsGoals
            goals={goals}
            loading={goalsLoading}
            onCreateGoal={handleCreateGoal}
            onUpdateGoal={handleUpdateGoal}
            onDeleteGoal={handleDeleteGoal}
          />

          {/* Achievement Badges */}
          <AchievementBadges />
        </div>

        {/* My Chores */}
        <div className="mb-8">
          <MyChores />
        </div>

        {/* Two Column Layout for Categories and Calculator */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Spending Categories Chart */}
          <SpendingCategories />
          
          {/* Interest Calculator */}
          <InterestCalculator currentBalance={user?.balance || 0} />
        </div>

        {/* Transaction History */}
        <Card className="shadow-lg rounded-2xl" data-testid="transactions-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-purple-500" />
              My Transactions
            </CardTitle>
            <CardDescription>See where your money comes and goes</CardDescription>
          </CardHeader>
          <CardContent>
            <TransactionList 
              transactions={transactions}
              showStatus={true}
              emptyMessage="No transactions yet"
              emptyIcon={PiggyBank}
            />
          </CardContent>
        </Card>
      </main>

      {/* Balance Distribution Dialog */}
      <Dialog open={distributeOpen} onOpenChange={setDistributeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="distribute-balance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-500" />
              Move Money to Pots
            </DialogTitle>
            <DialogDescription>
              Choose how to split your balance across your pots
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Balance */}
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg text-center">
              <p className="text-sm text-slate-600 mb-1">Your Balance</p>
              <p className="text-3xl font-bold text-amber-600">${user?.balance?.toFixed(2)}</p>
            </div>

            <p className="text-sm text-slate-600">
              Drag the sliders to choose how much goes to each pot:
            </p>
            
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <AllocationSlider
                spendPercent={distributeAllocation.spend}
                savePercent={distributeAllocation.save}
                givePercent={distributeAllocation.give}
                onChange={setDistributeAllocation}
              />
              <AllocationPreview 
                amount={user?.balance || 0}
                spend={distributeAllocation.spend}
                save={distributeAllocation.save}
                give={distributeAllocation.give}
              />
            </div>
            
            <Button 
              onClick={handleDistributeBalance}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              disabled={distributing}
              data-testid="confirm-distribute-btn"
            >
              {distributing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
              Move to Pots
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
